import get from 'lodash-es/get'
import { type ReactElement, useMemo } from 'react'

import { useFormikContext } from '../hooks/useFormikContext'
import {
  type ExistingValues,
  type ExistingValuesContextType,
  Provider,
} from './existingValuesContext'

type Props<T> = {
  existingValues?: T
  children:
    | ReactElement
    | ((
        currentContext: ExistingValuesContextType<T>,
      ) => ReactElement | null | false)
}

/**
 * Context Provider for managing existing values in forms.
 * A form can be wrapped with this provider to provide access to the existingValues object.
 * It allows components to retrieve values by name using the getExistingValue function
 * or access the entire existingValues object.
 */
const ExistingValuesProvider = <T extends ExistingValues>({
  children,
  existingValues,
}: Props<T>) => {
  const { initialValues } = useFormikContext()

  const value = useMemo(
    () =>
      ({
        existingValues,
        getExistingValue: (name) =>
          get(existingValues, name as string) ??
          get(initialValues, name as string),
      }) satisfies ExistingValuesContextType<T>,
    [existingValues, initialValues],
  )

  return (
    <Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </Provider>
  )
}

export { ExistingValuesProvider }
